// @ts-check
import './fonts.css';
import './intro.css';

import React from 'react';
import propTypes from 'prop-types';
import { ConnectedRouter } from 'connected-react-router';
import {
	StylesProvider,
	ThemeProvider as MuiThemeProvider,
} from '@material-ui/core/styles';
import './Mui6ClassNameGenerator'; // must be imported before theme and anything that consumes the theme

import {
	ThemeProvider as UcThemeProvider,
	CssBaseline,
} from '@liquidweb-dev/undercurrent';
import { Toaster } from '@liquidweb-dev/undercurrent/toast';
import { Provider as ReduxProvider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import MaintenanceModeGate from 'containers/structural/MaintenanceModeGate';
import ErrorBoundary from 'containers/structural/ErrorBoundary';
import RouteHandler from 'components/routers/RouteHandler';
import ChatMiawGate from 'containers/structural/ChatMiawGate';
import AppConfigGate from 'utility/appConfig/AppConfigGate';
import { IntroProvider } from 'containers/introjs';
import OidcProvider from 'modules/oidc/oidcProvider';
import { history, store } from './store';
import theme from './theme';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: { refetchOnWindowFocus: false, retry: false, staleTime: 500 },
	},
});

export function Providers({ children }) {
	return [
		(child) => <ReduxProvider store={store}>{child}</ReduxProvider>,
		(child) => (
			<QueryClientProvider client={queryClient}>
				<ReactQueryDevtools initialIsOpen={false} />
				{child}
			</QueryClientProvider>
		),
		(child) => <OidcProvider>{child}</OidcProvider>,
		(child) => <MuiThemeProvider theme={theme}>{child}</MuiThemeProvider>,
		(child) => <StylesProvider injectFirst>{child}</StylesProvider>,
		(child) => <ThemeProvider theme={theme}>{child}</ThemeProvider>,
		(child) => (
			<UcThemeProvider>
				<CssBaseline />
				<Toaster />
				{child}
			</UcThemeProvider>
		),
		(child) => <AppConfigGate>{child}</AppConfigGate>,
		(child) => <ConnectedRouter history={history}>{child}</ConnectedRouter>,
		(child) => <MaintenanceModeGate>{child}</MaintenanceModeGate>,
		(child) => <ChatMiawGate>{child}</ChatMiawGate>,
		(child) => <ErrorBoundary fullScreen>{child}</ErrorBoundary>,
		(child) => <IntroProvider>{child}</IntroProvider>,
	]
		.reverse()
		.reduce((child, provider) => provider(child), children);
}

Providers.propTypes = {
	children: propTypes.node.isRequired,
};

const App = () => (
	<Providers>
		<RouteHandler />
	</Providers>
);

export default App;
